import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center px-8 py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f5f3f0] via-white to-white"></div>
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-[#f5f3f0] rounded-lg">
                <span className="text-sm font-medium text-[#2d3748]">静的サイト制作サービス</span>
              </div>
              <h1 className="text-6xl font-bold text-[#1a2332] leading-tight">
                月額費用のない、<br />
                シンプルで速い<br />
                ウェブサイト制作
              </h1>
              <p className="text-xl text-[#4a5568] leading-relaxed">
                小規模店舗・個人事業主のための、更新頻度の低いサイトに最適化した制作サービス
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/contact"
                  className="px-10 py-4 bg-[#1a2332] text-white rounded-full font-medium hover:bg-[#2d3748] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  無料相談を申し込む
                </Link>
                <button
                  onClick={() => scrollToSection('pricing')}
                  className="px-10 py-4 text-[#1a2332] font-medium underline hover:text-[#4a5568] transition-colors whitespace-nowrap cursor-pointer"
                >
                  料金を見る
                </button>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="w-full h-[600px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=minimalist%20modern%20website%20mockup%20display%20on%20laptop%20screen%20with%20clean%20grid%20layout%20and%20simple%20geometric%20shapes%2C%20professional%20business%20aesthetic%2C%20soft%20lighting%2C%20white%20and%20beige%20color%20scheme%2C%20high%20quality%20digital%20design%20showcase%2C%20studio%20photography%20style&width=800&height=1000&seq=hero-visual-001&orientation=portrait"
                  alt="Trim-Pageのウェブサイト制作イメージ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Concept Section */}
      <section id="service" className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
              <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">Service Concept</span>
            </div>
            <h2 className="text-5xl font-bold text-[#2d3748]">Trim-Pageの思想</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-flashlight-line',
                title: '静的サイトの利点',
                description: 'Cloudflare Pagesを活用した高速配信により、訪問者に快適な閲覧体験を提供します。サーバー処理が不要なため、表示速度が圧倒的に速く、SEO評価も向上します。'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'ドメイン自己管理',
                description: 'ドメインの所有権は完全にお客様側で保持いただけます。制作会社に依存せず、将来的な移転や変更も自由に行えるため、長期的な安心感があります。'
              },
              {
                icon: 'ri-layout-grid-line',
                title: 'シンプル構造',
                description: '複雑なCMSやデータベースを排除した明快な設計により、セキュリティリスクを最小化。メンテナンスの手間も大幅に削減できます。'
              },
              {
                icon: 'ri-robot-2-line',
                title: 'AI活用による効率的な制作',
                description: '最新のAI技術を活用した効率的なページ制作により、高品質なデザインと短納期を両立。お客様のご要望を素早く形にすることができます。'
              }
            ].map((item, index) => (
              <div
                key={index}
                className="group p-8 bg-white border border-[#e2e8f0] rounded-xl hover:bg-[#1a2332] transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full mb-6 group-hover:bg-white/20 transition-colors">
                  <i className={`${item.icon} text-2xl text-[#1a2332] group-hover:text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#2d3748] mb-4 group-hover:text-white transition-colors">
                  {item.title}
                </h3>
                <p className="text-[#4a5568] leading-relaxed group-hover:text-white/90 transition-colors">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-32 px-8 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
              <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">Benefits</span>
            </div>
            <h2 className="text-5xl font-bold text-[#2d3748]">選ばれる理由</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Large Cards */}
            <div className="lg:col-span-2 space-y-6">
              <div className="p-10 bg-[#f5f3f0] rounded-2xl">
                <div className="w-16 h-16 flex items-center justify-center bg-[#1a2332] rounded-full mb-6">
                  <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#2d3748] mb-4">月額費用原則ゼロ</h3>
                <p className="text-lg text-[#4a5568] leading-relaxed">
                  初期費用のみで運用可能。Cloudflare Pages 無料プランなど商用利用可能な無料の仕組みを活用することで、月額のサーバー費用やCMSライセンス料が一切不要です。小規模事業者にとって、予測可能なコスト構造を実現します。
                </p>
                <p className="text-sm text-[#4a5568] mt-4 opacity-80">
                  ※利用サービスの価格変更や仕様変更により、有料化もしくは制限が発生する可能性があります。
                </p>
              </div>

              <div className="p-10 bg-[#f5f3f0] rounded-2xl">
                <div className="w-16 h-16 flex items-center justify-center bg-[#1a2332] rounded-full mb-6">
                  <i className="ri-rocket-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#2d3748] mb-4">高速表示</h3>
                <p className="text-lg text-[#4a5568] leading-relaxed">
                  静的ファイル配信の技術的優位性により、ページ読み込み時間を大幅に短縮。CDNを通じた世界中への高速配信で、訪問者の離脱率を低減し、コンバージョン率の向上に貢献します。
                </p>
              </div>
            </div>

            {/* Small Cards */}
            <div className="space-y-6">
              <div className="p-8 bg-white border border-[#e2e8f0] rounded-xl">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full mb-4">
                  <i className="ri-lock-line text-2xl text-[#1a2332]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#2d3748] mb-2">セキュリティ</h3>
                <p className="text-sm text-[#4a5568]">データベース不要で攻撃リスクを最小化</p>
              </div>

              <div className="p-8 bg-white border border-[#e2e8f0] rounded-xl">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full mb-4">
                  <i className="ri-settings-3-line text-2xl text-[#1a2332]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#2d3748] mb-2">シンプル運用</h3>
                <p className="text-sm text-[#4a5568]">複雑な管理画面やアップデートが不要</p>
              </div>

              <div className="p-8 bg-white border border-[#e2e8f0] rounded-xl">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full mb-4">
                  <i className="ri-leaf-line text-2xl text-[#1a2332]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#2d3748] mb-2">環境負荷低減</h3>
                <p className="text-sm text-[#4a5568]">サーバー負荷が少なくエコフレンドリー</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section id="comparison" className="py-32 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d3748] mb-4">ホームページホスティングサービス（WordPress）vs 静的サイト</h2>
            <p className="text-lg text-[#4a5568]">どちらが適しているかは、更新頻度と運用体制によります</p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full rounded-2xl overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-[#1a2332] text-white">
                  <th className="px-8 py-6 text-left font-bold text-lg">比較項目</th>
                  <th className="px-8 py-6 text-center font-bold text-lg">ホスティングサービス</th>
                  <th className="px-8 py-6 text-center font-bold text-lg">Trim-Page</th>
                  <th className="px-8 py-6 text-left font-bold text-lg">備考</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    item: '編集の容易性',
                    hosting: '◎',
                    trimpage: '△',
                    note: '頻繁な更新にはWordPressが有利'
                  },
                  {
                    item: '表示速度',
                    hosting: '△',
                    trimpage: '◎',
                    note: '静的サイトは圧倒的に高速'
                  },
                  {
                    item: '運用負荷',
                    hosting: '△',
                    trimpage: '◎',
                    note: 'アップデートやバックアップが不要'
                  },
                  {
                    item: '月額維持費',
                    hosting: '¥4,000〜',
                    trimpage: '¥0',
                    note: 'ドメイン費用のみ実費'
                  },
                  {
                    item: 'セキュリティ',
                    hosting: '△',
                    trimpage: '◎',
                    note: '攻撃対象となる要素が少ない'
                  },
                  {
                    item: '初期費用',
                    hosting: '¥50,000〜',
                    trimpage: '¥30,000〜',
                    note: 'AIデザインを用いることで低コストで提供'
                  }
                ].map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-[#f5f3f0]' : 'bg-white'}>
                    <td className="px-8 py-6 text-[#2d3748] font-medium">{row.item}</td>
                    <td className="px-8 py-6 text-center text-[#2d3748] font-bold text-lg">{row.hosting}</td>
                    <td className="px-8 py-6 text-center text-[#2d3748] font-bold text-lg">{row.trimpage}</td>
                    <td className="px-8 py-6 text-[#4a5568] text-sm">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {[
              {
                item: '編集の容易性',
                hosting: '◎',
                trimpage: '△',
                note: '頻繁な更新にはWordPressが有利'
              },
              {
                item: '表示速度',
                hosting: '△',
                trimpage: '◎',
                note: '静的サイトは圧倒的に高速'
              },
              {
                item: '運用負荷',
                hosting: '△',
                trimpage: '◎',
                note: 'アップデートやバックアップが不要'
              },
              {
                item: '月額維持費',
                hosting: '¥4,000〜',
                trimpage: '¥0',
                note: 'ドメイン費用のみ実費'
              },
              {
                item: 'セキュリティ',
                hosting: '△',
                trimpage: '◎',
                note: '攻撃対象となる要素が少ない'
              },
              {
                item: '初期費用',
                hosting: '¥50,000〜',
                trimpage: '¥30,000〜',
                note: 'AIデザインを用いることで低コストで提供'
              }
            ].map((row, index) => (
              <div key={index} className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-4 bg-[#1a2332]">
                  <span className="text-white font-bold">{row.item}</span>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#4a5568]">ホスティングサービス</span>
                    <span className="text-lg font-bold text-[#2d3748]">{row.hosting}</span>
                  </div>
                  <div className="h-px bg-[#e2e8f0]"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#4a5568]">Trim-Page</span>
                    <span className="text-lg font-bold text-[#2d3748]">{row.trimpage}</span>
                  </div>
                  <div className="h-px bg-[#e2e8f0]"></div>
                  <p className="text-xs text-[#4a5568] pt-1">{row.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 px-8 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
              <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">Pricing</span>
            </div>
            <h2 className="text-5xl font-bold text-[#2d3748] mb-4">明瞭な料金体系</h2>
            <p className="text-lg text-[#4a5568]">初期費用のみ、月額費用は不要です</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: '基本制作',
                price: '¥30,000',
                description: 'スタンダードなウェブサイト制作',
                features: [
                  'Astroベースの静的サイト構築',
                  'レスポンシブデザイン対応',
                  '1ページの制作',
                  'Cloudflare Pages設定',
                  '基本的なSEO対策'
                ]
              },
              {
                name: 'ページ追加オプション',
                price: '¥10,000',
                description: '追加ページの制作',
                features: [
                  '1ページあたりの料金',
                  'レスポンシブデザイン対応',
                  '既存デザインに準拠',
                  'ページ間のナビゲーション設定',
                  'SEO対策実施'
                ]
              },
              {
                name: 'フォーム連携オプション',
                price: '¥10,000',
                description: '外部フォームサービスとの連携',
                features: [
                  'SSGFormなど外部サービス連携',
                  '1フォームあたりの料金',
                  'Google reCAPTCHA対応',
                  'フォーム設置・動作確認',
                  '送信テスト実施'
                ]
              },
              {
                name: 'CMS連携オプション',
                price: '+¥10,000',
                description: '記事投稿機能の追加',
                features: [
                  'microCMS基本設定',
                  'コンテンツモデル設計',
                  '記事一覧・詳細ページ実装',
                  '画像アップロード機能',
                  '管理画面操作レクチャー',
                  '初期記事投稿サポート'
                ]
              },
              {
                name: 'GitHub支援オプション',
                price: '+¥10,000',
                description: 'バージョン管理環境の構築',
                features: [
                  'GitHubリポジトリ作成',
                  'Cloudflare Pages連携設定',
                  '自動デプロイフロー構築',
                  'Git操作基礎レクチャー',
                  'ブランチ運用ガイド提供',
                  '更新手順マニュアル作成'
                ]
              },
              {
                name: 'microCMS支援オプション',
                price: '+¥10,000',
                description: 'CMS運用の完全サポート',
                features: [
                  'microCMSアカウント設定代行',
                  'カスタムフィールド設計',
                  'Webhook設定',
                  'プレビュー機能実装',
                  '運用マニュアル作成',
                  '1ヶ月間の運用サポート'
                ]
              }
            ].map((plan, index) => (
              <div
                key={index}
                className="group p-10 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <h3 className="text-2xl font-bold text-[#1a2332] mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-[#2d3748] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                  {plan.price}
                </div>
                <div className="h-px bg-[#f5f3f0] my-6"></div>
                <p className="text-[#4a5568] mb-6">{plan.description}</p>
                <ul className="space-y-3">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-lg text-[#1a2332]"></i>
                      </div>
                      <span className="text-sm text-[#4a5568]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* 注釈 */}
          <div className="mt-12 text-center">
            <p className="text-sm text-[#4a5568]">※正式なお見積り後に契約締結となります</p>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section id="flow" className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
              <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">Flow</span>
            </div>
            <h2 className="text-5xl font-bold text-[#2d3748]">制作までの流れ</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              {
                step: '01',
                icon: 'ri-mail-send-line',
                title: '無料相談・お問い合わせ',
                description: 'フォームよりご相談内容をお知らせください。'
              },
              {
                step: '02',
                icon: 'ri-discuss-line',
                title: 'ヒアリング・要件整理',
                description: '必要に応じて詳細を確認いたします。'
              },
              {
                step: '03',
                icon: 'ri-file-list-3-line',
                title: '個別お見積りの提示',
                description: '内容に応じた正式なお見積りをご案内します。'
              },
              {
                step: '04',
                icon: 'ri-file-text-line',
                title: 'ご契約',
                description: 'お見積り内容に合意後、契約成立となります。'
              },
              {
                step: '05',
                icon: 'ri-tools-line',
                title: '制作着手',
                description: 'ご入金確認後、制作作業を開始いたします。'
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1a2332] rounded-full mb-6">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="mb-3">
                    <span className="inline-block px-4 py-1 bg-[#f5f3f0] rounded-full text-sm font-bold text-[#1a2332]">
                      STEP {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-[#2d3748] mb-3">{item.title}</h3>
                  <p className="text-sm text-[#4a5568] leading-relaxed">{item.description}</p>
                </div>
                {/* Arrow for desktop */}
                {index < 4 && (
                  <div className="hidden md:block absolute top-10 -right-4 text-[#e2e8f0]">
                    <i className="ri-arrow-right-line text-3xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-32 px-8 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Text */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
                <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">After Launch Support</span>
              </div>
              <h2 className="text-5xl font-bold text-[#2d3748] leading-tight">
                公開後も<br />安心
              </h2>
              <p className="text-lg text-[#4a5568] leading-relaxed">
                1か月間の無償サポート期間を設けています。サイト公開後の軽微な修正や操作方法のご質問に対応いたします。以降はスポット相談で柔軟に対応可能です。
              </p>
              <Link
                to="/support"
                className="inline-block px-8 py-3 text-[#1a2332] font-medium underline hover:text-[#4a5568] transition-colors whitespace-nowrap cursor-pointer"
              >
                サポート詳細を見る
              </Link>
            </div>

            {/* Right Support Card */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="p-8 border-b border-[#e2e8f0]">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full">
                    <i className="ri-calendar-check-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#2d3748]">無償期間</h3>
                    <p className="text-[#4a5568]">公開後1か月</p>
                  </div>
                </div>
                <p className="text-sm text-[#4a5568] leading-relaxed">
                  サイト公開から1か月間は、テキスト修正や画像差し替えなどの軽微な変更に無償で対応いたします。
                </p>
              </div>

              <div className="p-8 border-b border-[#e2e8f0]">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full">
                    <i className="ri-chat-3-line text-2xl text-[#1a2332]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#2d3748]">スポット相談</h3>
                    <p className="text-[#4a5568]">¥5,000/回</p>
                  </div>
                </div>
                <p className="text-sm text-[#4a5568] leading-relaxed">
                  無償期間終了後も、必要に応じてスポットでのご相談を承ります。内容に応じて柔軟に対応いたします。
                </p>
              </div>

              <div className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3f0] rounded-full">
                    <i className="ri-list-check-2 text-2xl text-[#1a2332]"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#2d3748]">対応範囲</h3>
                </div>
                <ul className="space-y-2">
                  {['テキスト・画像の修正', '操作方法のご質問', '軽微なレイアウト調整', 'トラブルシューティング'].map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-[#1a2332]"></i>
                      <span className="text-sm text-[#4a5568]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-32 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
              <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">FAQ</span>
            </div>
            <h2 className="text-5xl font-bold text-[#2d3748]">よくあるご質問</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                question: '本当に月額費用はかかりませんか？',
                answer: 'はい、Cloudflare Pagesの無料プランを活用するため、月額のサーバー費用は発生しません。ただし、ドメイン費用（年間1,000〜2,000円程度）は別途実費でご負担いただきます。また、microCMSなど外部サービスを利用する場合は、そちらの料金が発生する場合があります。'
              },
              {
                question: 'ページ制作は人間のデザイナーが行うのですか？',
                answer: '原則としてAI技術を活用してページ制作を行います。そのため、他のサイトと似たデザインになる可能性があります。人間のデザイナーによるオリジナルデザインをご希望の場合は、個別にご相談ください。ただし、その場合は料金が値上がりいたします。'
              },
              {
                question: '既存サイトからの移行は可能ですか？',
                answer: '可能です。ただし、既存サイトの構造や内容によって作業量が大きく異なるため、個別にお見積もりさせていただきます。WordPressからの移行の場合、コンテンツの移植やデザインの再現度によって費用が変動します。まずは無料相談でご相談ください。'
              },
              {
                question: 'Astroとは何ですか？',
                answer: 'Astroは、高速な静的サイトを構築するための最新のWebフレームワークです。必要最小限のJavaScriptのみを配信するため、ページの読み込み速度が非常に速く、SEOにも有利です。技術的な知識がなくても、納品後の運用に支障はありません。'
              },
              {
                question: 'HTML納品は可能ですか？',
                answer: 'はい、可能です。Astroで構築したサイトは、最終的に静的なHTMLファイルとして出力されます。ご希望に応じて、ビルド済みのHTMLファイル一式を納品することも可能です。ただし、その場合は今後の更新作業が複雑になる可能性がありますので、GitHubでの管理をお勧めしています。'
              },
              {
                question: 'ドメイン費用は含まれますか？',
                answer: 'いいえ、ドメイン費用は含まれしておりません。ドメインの所有権をお客様に完全に帰属させるため、お客様ご自身で取得・管理していただきます。ドメイン取得のサポートや推奨レジストラのご案内は無償で行います。年間費用は一般的に1,000〜2,000円程度です。'
              },
              {
                question: '更新頻度が高い場合はどうすればよいですか？',
                answer: '週に数回以上の更新が必要な場合は、WordPressなどのCMSの方が適している可能性があります。ただし、microCMS連携オプションをご利用いただければ、ブログ記事などの頻繁な更新にも対応可能です。お客様の運用スタイルに合わせて最適な方法をご提案いたします。'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white border border-[#e2e8f0] rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between hover:bg-[#f5f3f0] transition-colors cursor-pointer"
                  style={{ backgroundColor: openFaq === index ? '#f5f3f0' : 'transparent' }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#1a2332] rounded-full flex-shrink-0">
                      <span className="text-white font-bold text-sm">Q</span>
                    </div>
                    <span className="font-bold text-[#2d3748] text-left">{faq.question}</span>
                  </div>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-[#2d3748] flex-shrink-0`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-8 py-6 bg-[#f5f3f0]">
                    <p className="text-[#4a5568] leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notice Section */}
      <section className="py-20 px-8 bg-[#f5f3f0]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-2xl text-[#d97706]"></i>
            </div>
            <h2 className="text-2xl font-bold text-[#2d3748]">ご契約前の確認事項</h2>
          </div>
          <ul className="space-y-3 ml-10">
            {[
              'ドメイン費用（年間1,000〜2,000円程度）はお客様の実費負担となります',
              'microCMSなど外部サービスのご契約は、お客様名義で行っていただきます',
              '週に数回以上の更新が必要な場合は、WordPressの方が適している可能性があります',
              'デザインの大幅な変更や追加ページの制作は、別途お見積もりとなります',
              '制作期間は通常2〜4週間程度ですが、内容により変動する場合があります',
              '納品後のサイト運用に関する技術サポートは、無償期間終了後は有償となります'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#2d3748] rounded-full flex-shrink-0 mt-2"></div>
                <span className="text-[#4a5568] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2332] text-white py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* Left Column */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/4944a4ba-c62a-4964-bf85-4ed6714cc305.png" 
                  alt="Trim-Page Logo" 
                  className="w-10 h-10 object-contain"
                />
                <h3 className="text-3xl font-bold">Trim-Page</h3>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                小規模店舗・個人事業主のための、月額費用のない静的サイト制作サービス
              </p>
              <div className="flex gap-4">
                {['ri-twitter-x-line', 'ri-facebook-circle-line', 'ri-instagram-line', 'ri-linkedin-box-line'].map((icon, index) => (
                  <a
                    key={index}
                    href="#"
                    className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <i className={`${icon} text-lg`}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Middle Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Contact</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white/60 mb-1">メールアドレス</p>
                  <a href="mailto:info@trim-page.com" className="text-white hover:text-white/80 transition-colors">
                    info@trim-page.com
                  </a>
                </div>
                <div>
                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('contact');
                    }}
                    className="text-white underline hover:text-white/80 transition-colors cursor-pointer"
                  >
                    お問い合わせフォーム
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Information</h4>
              <div className="space-y-4">
                <Link to="/about-operator" className="block text-white hover:text-white/80 transition-colors">
                  運営者情報
                </Link>
                <Link
                  to="/tokushoho"
                  className="block text-white hover:text-white/80 transition-colors"
                >
                  特定商取引法に基づく表記
                </Link>
                <Link
                  to="/privacy-policy"
                  className="block text-white hover:text-white/80 transition-colors"
                >
                  プライバシーポリシー
                </Link>
                <Link
                  to="/support"
                  className="block text-white hover:text-white/80 transition-colors"
                >
                  サポートについて
                </Link>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">© 2025 Trim-Page. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors">
                利用規約
              </a>
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors">
                サイトマップ
              </a>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
